// GPS status singleton — observable "last GPS fix" + "last GPS error" used by
// the OfflineTrackingStatus panel so travelers can see proof that pings are
// being captured locally while offline.
//
// Pure JS / OTA-safe. No native plugin. Persists lastFixAt across reloads of
// the bundled shell so the panel doesn't say "waiting for GPS" right after
// the user reopens the app.
import { useSyncExternalStore } from "react";

const LS_LAST_FIX_AT = "roava.gps.lastFixAt";
const LS_LAST_LAT = "roava.gps.lastLat";
const LS_LAST_LNG = "roava.gps.lastLng";
const LS_LAST_ACC = "roava.gps.lastAccuracy";

export type GpsStatus = {
  lastFixAt: number | null;          // epoch ms of last successful position
  lastLat: number | null;
  lastLng: number | null;
  lastAccuracy: number | null;       // meters
  lastError: string | null;          // most recent watchPosition error message
  lastErrorAt: number | null;
};

let state: GpsStatus = {
  lastFixAt: null,
  lastLat: null,
  lastLng: null,
  lastAccuracy: null,
  lastError: null,
  lastErrorAt: null,
};

function num(v: string | null): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function hydrate() {
  if (typeof localStorage === "undefined") return;
  state = {
    ...state,
    lastFixAt: num(localStorage.getItem(LS_LAST_FIX_AT)),
    lastLat: num(localStorage.getItem(LS_LAST_LAT)),
    lastLng: num(localStorage.getItem(LS_LAST_LNG)),
    lastAccuracy: num(localStorage.getItem(LS_LAST_ACC)),
  };
}
hydrate();

const listeners = new Set<() => void>();
let snapshot: GpsStatus = { ...state };
function notify() {
  snapshot = { ...state };
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

export function getGpsStatus(): GpsStatus {
  return snapshot;
}

export function markGpsFix(lat: number, lng: number, accuracy: number | null, ts: number = Date.now()): void {
  // Monotonic: never move lastFixAt backwards. Out-of-order drains of older
  // native-queue points shouldn't make the chip look stale.
  if (!Number.isFinite(ts)) return;
  if (state.lastFixAt != null && ts <= state.lastFixAt) {
    // Still clear errors — a fix arriving (even an old one) means GPS is alive.
    state.lastError = null;
    state.lastErrorAt = null;
    notify();
    return;
  }
  state.lastFixAt = ts;
  state.lastLat = lat;
  state.lastLng = lng;
  state.lastAccuracy = accuracy;
  state.lastError = null;
  state.lastErrorAt = null;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LS_LAST_FIX_AT, String(ts));
      localStorage.setItem(LS_LAST_LAT, String(lat));
      localStorage.setItem(LS_LAST_LNG, String(lng));
      localStorage.setItem(LS_LAST_ACC, accuracy == null ? "" : String(accuracy));
    }
  } catch { /* ignore quota */ }
  notify();
}

/**
 * Advance only the lastFixAt timestamp from an authoritative external source
 * (native queue's newest point). Lat/lng/accuracy stay as the last known
 * values — the chip / health watcher only care about fix age. Monotonic.
 */
export function bumpGpsFixTimestamp(ts: number): void {
  if (!Number.isFinite(ts)) return;
  if (state.lastFixAt != null && ts <= state.lastFixAt) return;
  state.lastFixAt = ts;
  state.lastError = null;
  state.lastErrorAt = null;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LS_LAST_FIX_AT, String(ts));
    }
  } catch { /* ignore quota */ }
  notify();
}

export function markGpsError(msg: string, ts: number = Date.now()): void {
  state.lastError = msg;
  state.lastErrorAt = ts;
  notify();
}

export function useGpsStatus(): GpsStatus {
  return useSyncExternalStore(
    (fn) => { listeners.add(fn); return () => { listeners.delete(fn); }; },
    () => snapshot,
    () => snapshot,
  );
}
